import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { BadgeCheck, Loader2, Rss, ShieldCheck, UserRound } from "lucide-react";

import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageMeta } from "@/components/site/PageMeta";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ErrorState } from "@/components/site/ErrorState";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { signPublishedStoryImages } from "@/lib/story-image";
import { STORIES_FEED_TITLE, STORIES_FEED_URL } from "@/lib/feeds";

const PAGE_SIZE = 9;

type PublishedStory = {
  id: string;
  full_name: string;
  location: string | null;
  story_title: string;
  story_body: string;
  consent_publish_name: boolean;
  published_at: string | null;
  image_path: string | null;
};

const excerpt = (body: string, max = 220) => {
  const clean = body.replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  return `${clean.slice(0, clean.lastIndexOf(" ", max) > 0 ? clean.lastIndexOf(" ", max) : max)}…`;
};

const authorName = (story: PublishedStory) =>
  story.consent_publish_name && story.full_name.trim() ? story.full_name.trim() : "Shared anonymously";

const publishedDate = (value: string | null) =>
  value ? new Date(value).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : null;

const TrustLabel = ({
  icon: Icon,
  children,
  tone = "muted",
}: {
  icon: typeof BadgeCheck;
  children: React.ReactNode;
  tone?: "muted" | "verified";
}) => (
  <span
    className={cn(
      "inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-medium",
      tone === "verified"
        ? "border-primary/30 bg-primary/10 text-primary"
        : "border-border bg-muted text-muted-foreground",
    )}
  >
    <Icon className="h-3 w-3" aria-hidden />
    {children}
  </span>
);

const Stories = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = Number.parseInt(searchParams.get("page") ?? "1", 10);
  const page = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1;

  const [stories, setStories] = useState<PublishedStory[]>([]);
  const [total, setTotal] = useState(0);
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const from = (page - 1) * PAGE_SIZE;
    const { data, count, error: err } = await supabase
      .from("story_submissions")
      .select("id, full_name, location, story_title, story_body, consent_publish_name, published_at, image_path", {
        count: "exact",
      })
      .eq("status", "published")
      .not("published_at", "is", null)
      .order("published_at", { ascending: false })
      .range(from, from + PAGE_SIZE - 1);

    if (err) {
      setError("We couldn't load the stories right now. Please try again in a moment.");
      setStories([]);
      setTotal(0);
    } else {
      const rows = (data as PublishedStory[]) ?? [];
      setStories(rows);
      setTotal(count ?? 0);
      // Signed URLs only resolve for images whose story is published.
      setImageUrls(await signPublishedStoryImages(rows.map((r) => r.image_path ?? "")));
    }
    setLoading(false);
  }, [page]);

  useEffect(() => {
    void load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const goToPage = (next: number) => {
    const clamped = Math.min(Math.max(next, 1), totalPages);
    setSearchParams(clamped === 1 ? {} : { page: String(clamped) });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const pageNumbers = useMemo(
    () => Array.from({ length: totalPages }, (_, i) => i + 1).filter((n) => Math.abs(n - page) <= 2 || n === 1 || n === totalPages),
    [totalPages, page],
  );

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PageMeta
        title="Stories of Change — Real Voices | DrSullySpeaks"
        description="Published, consent-verified stories from families, advocates, and leaders who made change with Dr. RD 'Sully' Sullivan. Reviewed and approved before publication."
        path="/stories"
        feed={{ url: STORIES_FEED_URL, title: STORIES_FEED_TITLE }}
      />
      <SiteNav />

      <header className="relative bg-hero pt-32 md:pt-40 pb-14 md:pb-18 border-b border-border overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,hsl(var(--gold)/0.08),transparent_50%)]"
        />
        <div className="container relative mx-auto max-w-4xl px-4 text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Community Voices</p>
          <h1 className="mt-3 text-4xl md:text-5xl font-bold tracking-tight">Stories of Change</h1>
          <p className="mt-4 text-base md:text-lg text-muted-foreground">
            Every story below was submitted voluntarily, reviewed by a moderator, and published only with the writer's
            written consent.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            <TrustLabel icon={ShieldCheck} tone="verified">
              Moderator reviewed
            </TrustLabel>
            <TrustLabel icon={BadgeCheck} tone="verified">
              Consent on file
            </TrustLabel>
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button asChild size="lg">
              <Link to="/share-your-story">Share your story</Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <a href={STORIES_FEED_URL} target="_blank" rel="noopener noreferrer">
                <Rss className="mr-2 h-4 w-4" aria-hidden />
                Subscribe via RSS
              </a>
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto max-w-6xl px-4 py-14 md:py-20">
        <h2 className="sr-only">Published stories</h2>

        {loading && (
          <div className="flex items-center justify-center py-20 text-muted-foreground">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden />
            Loading stories…
          </div>
        )}

        {!loading && error && (
          <ErrorState
            kicker="Couldn't load stories"
            title="Stories aren't loading right now"
            description={error}
            onRetry={() => void load()}
          />
        )}

        {!loading && !error && stories.length === 0 && (
          <div className="mx-auto max-w-md text-center">
            <h3 className="text-lg font-semibold">No published stories yet</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Stories appear here once they've been reviewed and approved. Yours could be the first.
            </p>
            <Button asChild className="mt-6">
              <Link to="/share-your-story">Share your story</Link>
            </Button>
          </div>
        )}

        {!loading && !error && stories.length > 0 && (
          <>
            <p className="mb-6 text-sm text-muted-foreground">
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total} published{" "}
              {total === 1 ? "story" : "stories"}
            </p>

            <ul className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {stories.map((story) => (
                <li key={story.id}>
                  <Card className="h-full">
                    <CardHeader className="space-y-2">
                      <div className="flex flex-wrap gap-1.5">
                        <TrustLabel icon={ShieldCheck} tone="verified">
                          Moderator approved
                        </TrustLabel>
                        {story.consent_publish_name ? (
                          <TrustLabel icon={BadgeCheck} tone="verified">
                            Named with consent
                          </TrustLabel>
                        ) : (
                          <TrustLabel icon={UserRound}>Name withheld</TrustLabel>
                        )}
                      </div>
                      <CardTitle className="text-lg leading-snug">{story.story_title}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {story.image_path && imageUrls[story.image_path] ? (
                        <img
                          src={imageUrls[story.image_path]}
                          alt={`Photo shared with the story “${story.story_title}”`}
                          loading="lazy"
                          className="aspect-video w-full rounded-lg border border-border object-cover"
                        />
                      ) : null}
                      <p className="text-sm leading-relaxed text-muted-foreground">{excerpt(story.story_body)}</p>
                      <div className="border-t border-border pt-3 text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">{authorName(story)}</span>
                        {story.location ? ` · ${story.location}` : ""}
                        {publishedDate(story.published_at) ? ` · ${publishedDate(story.published_at)}` : ""}
                      </div>
                    </CardContent>
                  </Card>
                </li>
              ))}
            </ul>

            {totalPages > 1 && (
              <nav aria-label="Stories pagination" className="mt-12 flex flex-wrap items-center justify-center gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => goToPage(page - 1)}>
                  Previous
                </Button>
                {pageNumbers.map((n, i) => {
                  const prev = pageNumbers[i - 1];
                  return (
                    <span key={n} className="flex items-center gap-2">
                      {prev && n - prev > 1 ? <span className="text-muted-foreground">…</span> : null}
                      <Button
                        variant={n === page ? "default" : "outline"}
                        size="sm"
                        aria-current={n === page ? "page" : undefined}
                        onClick={() => goToPage(n)}
                      >
                        {n}
                      </Button>
                    </span>
                  );
                })}
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => goToPage(page + 1)}>
                  Next
                </Button>
              </nav>
            )}
          </>
        )}

        <aside className="mt-16 rounded-lg border border-border bg-muted/30 p-6 text-center">
          <Badge variant="outline" className="mb-3">
            How stories get here
          </Badge>
          <p className="mx-auto max-w-2xl text-sm text-muted-foreground">
            Submissions are held privately until a moderator reviews them. Names appear only when the writer explicitly
            consented; otherwise the story is published anonymously. Nothing is edited for meaning.
          </p>
        </aside>
      </main>

      <SiteFooter />
    </div>
  );
};

export default Stories;
